import type { RegistryDevice } from "@/types";

/**
 * Device utility functions for PPA components
 */

/**
 * Get the technology type for a device based on its ID
 * Returns the technology string that can be used with TechnologyIcon component
 */
export function getDeviceTechnology(
  deviceId: string | number,
  registryDevices: RegistryDevice[]
): string {
  const device = registryDevices?.find(
    (d) => d.id.toString() === deviceId.toString()
  );
  if (!device || !device.technology) return "UNKNOWN";

  // Normalize the technology to match TechnologyIcon expected values
  const tech = device.technology.toUpperCase();
  if (tech.includes("WIND")) return "WIND";
  if (
    tech.includes("SOLAR") ||
    tech.includes("PHOTOVOLTAIC") ||
    tech.includes("PV")
  )
    return "SOLAR";
  if (tech.includes("HYDRO") || tech.includes("WATER")) return "HYDRO";
  if (tech.includes("MARINE")) return "MARINE";
  if (
    tech.includes("BIOMASS") ||
    tech.includes("BIOGAS") ||
    tech.includes("BIO")
  )
    return "BIOMASS";
  if (tech.includes("NUCLEAR")) return "NUCLEAR";

  return "UNKNOWN"; // Default for unknown technologies
}

/**
 * Get the display name for a device
 */
export function getDeviceName(
  deviceId: string,
  registryDevices: RegistryDevice[]
): string {
  if (!registryDevices || !Array.isArray(registryDevices)) {
    return deviceId;
  }

  const device = registryDevices.find((d) => d.id.toString() === deviceId);
  return device ? device.device_name : deviceId;
}

/**
 * Convert devices to select options for MultiSelectComponent
 */
export function createDeviceOptions(registryDevices: RegistryDevice[]) {
  if (!registryDevices || !Array.isArray(registryDevices)) {
    return [];
  }

  return registryDevices
    .filter((device) => device && typeof device.id !== "undefined")
    .map((device) => ({
      label: device.device_name || `Device ${device.id}`,
      value: device.id.toString(),
      description: device.device_eic || "",
    }));
}

/**
 * Generate device search placeholder text based on available devices
 */
export function getDeviceSearchPlaceholder(
  availableCount: number,
  totalCount: number,
  usedCount: number,
  t: (key: string, params?: any) => string
): string {
  // If no devices are loaded yet
  if (totalCount === 0) {
    return t("portfolio_instructions.loading_devices");
  }

  if (availableCount === 0 && usedCount > 0) {
    return t("portfolio_instructions.devices_blocked_by_other_ppas");
  }

  if (availableCount === 0) {
    return t("portfolio_instructions.no_devices_available");
  }

  return t("portfolio_instructions.search_devices_placeholder", {
    count: availableCount,
  });
}
